/**
 * What is underneath the aeroplane — the one thing the flight core cannot know
 * on its own.
 *
 * `GroundQuery` is a deliberately tiny injectable seam. `step` takes one and
 * asks it two questions: how high the ground is at a world (x, z), and what
 * kind of surface it is. Nothing else about terrain enters the sim, and the
 * query is a *pure function of position* — it never appears in `SimState`, is
 * never snapshotted, and never draws randomness, so a state hash stays a
 * function of the flight alone (AD-007a).
 *
 * **The default is flat datum**, which is exactly what this port's world is
 * today (`apps/web`'s ground plane is one flat quad at y ≈ 0). The elevation
 * lane is building the real query out of `.GI` hill data; when it lands it
 * implements this interface and `/fly` passes it to `stepEncounter` instead of
 * `FLAT_GROUND`, and nothing in the physics changes.
 *
 * Axes: `x` and `z` are the two *horizontal* world axes (three.js-conventional
 * Y-up, +X east, −Z north — AD-007c). A provider whose own data is indexed
 * (x, y) with y horizontal maps its second axis onto `z` here.
 *
 * Surfaces exist because the manual makes the distinction load-bearing twice:
 * a pilot who cannot find his aerodrome "may land in a field on your side of
 * the lines" (04-gameplay), and a *level* field is worth remarking on —
 * Jasta 40's new aerodrome "proved to be so level that during the last weeks of
 * the war not a single Fokker nosed over on landing or taxiing, a fairly common
 * occurrence in the past" (06-heroes-aftermath). So a prepared aerodrome is
 * kinder to an undercarriage than a farmer's field, and both are kinder than
 * broken ground. The multipliers below are port-side (`undercarriage.ts` says
 * so in its register); the *ordering* is the manual's.
 */

export const SURFACE_KINDS = ["aerodrome", "field", "rough", "water"] as const;

export type SurfaceKind = (typeof SURFACE_KINDS)[number];

/** Everything one ground sample tells the physics. */
export interface GroundSample {
  readonly heightM: number;
  readonly surface: SurfaceKind;
}

export interface GroundQuery {
  /** Terrain height in metres above datum at the horizontal world position (x, z). */
  groundHeightAt(x: number, z: number): number;
  /** What is underfoot there. Optional: a provider with only elevation data omits it and everything reads as `DEFAULT_SURFACE`. */
  surfaceAt?(x: number, z: number): SurfaceKind;
}

/** What an elevation-only provider's ground counts as: open farmland, not a prepared strip. */
export const DEFAULT_SURFACE: SurfaceKind = "field";

/** Datum everywhere — the port's world as it stands, and the contract's default. */
export const FLAT_GROUND: GroundQuery = { groundHeightAt: () => 0 };

/** Flat ground at a stated height, for a mission or a test that wants a non-zero datum. */
export function flatGroundAt(heightM: number): GroundQuery {
  return { groundHeightAt: () => heightM };
}

/** One sample, with the optional half filled in. */
export function sampleGround(ground: GroundQuery, x: number, z: number): GroundSample {
  return {
    heightM: ground.groundHeightAt(x, z),
    surface: ground.surfaceAt?.(x, z) ?? DEFAULT_SURFACE,
  };
}

/** A prepared landing ground: an aerodrome, or any patch a mission declares flyable-off. */
export interface LandingField {
  readonly x: number;
  readonly z: number;
  readonly radiusM: number;
}

export function horizontalRangeM(field: LandingField, x: number, z: number): number {
  return Math.hypot(x - field.x, z - field.z);
}

/** The field this position is over, or `undefined` out in the country. */
export function fieldUnder(
  fields: readonly LandingField[],
  x: number,
  z: number,
): LandingField | undefined {
  return fields.find((field) => horizontalRangeM(field, x, z) <= field.radiusM);
}

/**
 * Stamps prepared aerodromes onto an elevation provider. The height comes
 * through unchanged — flattening the terrain under a field is the elevation
 * lane's business, not this seam's — and only the surface *kind* is overridden.
 */
export function withLandingFields(base: GroundQuery, fields: readonly LandingField[]): GroundQuery {
  return {
    groundHeightAt: (x, z) => base.groundHeightAt(x, z),
    surfaceAt: (x, z) =>
      fieldUnder(fields, x, z) ? "aerodrome" : (base.surfaceAt?.(x, z) ?? DEFAULT_SURFACE),
  };
}

/**
 * Stamps the sea onto a provider — the wrapper that finally makes `"water"`
 * reachable.
 *
 * `docs/engineering/london-sea-placements.md` §3.3: `SurfaceKind "water"` and
 * its gear limit of 0 have existed since this seam did, and **no provider ever
 * returned it**, because the only one ever constructed was
 * `withLandingFields`, which answers `"aerodrome"` or `"field"`. A coastal
 * region's `isWater` goes in here (`apps/web/src/world/water-mask.ts`), and a
 * gear-down arrival on the North Sea stops being a landing on a farmer's
 * field.
 *
 * **Outermost on purpose.** Water beats a landing field, never the other way
 * round: nothing should ever be *emplaced* in water — that is what the region
 * origin and the aerodrome anchoring guarantee upstream — but if a field's
 * circle ever did overlap the sea, the sea is the truth under the wheels. The
 * height comes through untouched: the datum is flat (world-terrain.md §5) and
 * sea level is the datum.
 *
 * This does not stop a player ditching. It is what makes ditching *mean*
 * something — a real enough end to a WW1 sortie that the manual's
 * "land in a field on your side of the lines" has a wetter sibling.
 */
export function withWater(base: GroundQuery, isWater: (x: number, z: number) => boolean): GroundQuery {
  return {
    groundHeightAt: (x, z) => base.groundHeightAt(x, z),
    surfaceAt: (x, z) => (isWater(x, z) ? "water" : (base.surfaceAt?.(x, z) ?? DEFAULT_SURFACE)),
  };
}

/**
 * How much punishment a surface takes off (or adds to) the undercarriage's
 * limits: 1 is the prepared aerodrome those limits are quoted for, and a lower
 * number means the same arrival breaks more. Port-side numbers; see the module
 * doc for the ordering's provenance.
 */
export const SURFACE_GEAR_LIMIT_SCALE: Readonly<Record<SurfaceKind, number>> = {
  aerodrome: 1,
  field: 0.8,
  rough: 0.55,
  water: 0,
};

/** Rolling resistance relative to a prepared strip. Ditching is not a roll at all. */
export const SURFACE_ROLLING_RESISTANCE_SCALE: Readonly<Record<SurfaceKind, number>> = {
  aerodrome: 1,
  field: 1.6,
  rough: 2.6,
  water: 6,
};
